import { useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, GearSix, Check, X } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import api from "../lib/api";

/**
 * Barra de pestañas del Dashboard.
 * - `tabs`: array {id, nombre, widget_keys[]}
 * - `activeId`: id de la tab activa
 * - `catalog`: [{key, label, group}] — widgets disponibles
 * - `onChange`: (id) => void — al cambiar de tab
 * - `onReload`: () => void — pedir a la página que refetchee tabs
 */
export default function DashboardTabsBar({ tabs, activeId, catalog, onChange, onReload, canEdit = true }) {
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [configOpen, setConfigOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const active = tabs.find((t) => t.id === activeId);

  const crear = async () => {
    const nombre = window.prompt("Nombre de la nueva pestaña:", "");
    if (!nombre || !nombre.trim()) return;
    try {
      const { data } = await api.post("/dashboard/tabs", {
        nombre: nombre.trim(),
        widget_keys: catalog.map((w) => w.key),
      });
      toast.success(`Pestaña "${data.nombre}" creada`);
      onReload?.(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    }
  };

  const iniciarRename = (t) => {
    setRenamingId(t.id);
    setRenameValue(t.nombre);
  };

  const confirmarRename = async () => {
    if (!renamingId) return;
    const nombre = renameValue.trim();
    if (!nombre) return setRenamingId(null);
    try {
      await api.put(`/dashboard/tabs/${renamingId}`, { nombre });
      setRenamingId(null);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo renombrar");
    }
  };

  const borrar = async (t) => {
    if (tabs.length <= 1) return toast.warning("No podés borrar la única pestaña");
    if (!window.confirm(`¿Eliminar la pestaña "${t.nombre}"?`)) return;
    try {
      await api.delete(`/dashboard/tabs/${t.id}`);
      toast.success("Pestaña eliminada");
      // Si borramos la activa, cambiamos a la primera
      const next = tabs.find((x) => x.id !== t.id);
      onReload?.(next?.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const toggleWidget = async (widgetKey) => {
    if (!active) return;
    setSaving(true);
    const keys = new Set(active.widget_keys || []);
    keys.has(widgetKey) ? keys.delete(widgetKey) : keys.add(widgetKey);
    try {
      await api.put(`/dashboard/tabs/${active.id}`, { widget_keys: [...keys] });
      onReload?.(active.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  // Widgets agrupados para el diálogo de configuración
  const groups = catalog.reduce((acc, w) => {
    (acc[w.group] ||= []).push(w);
    return acc;
  }, {});

  return (
    <div className="flex items-center gap-1 flex-wrap min-w-0" data-testid="dashboard-tabs-bar">
      {tabs.map((t) => {
        const isActive = t.id === activeId;
        const isRenaming = renamingId === t.id;
        return (
          <div key={t.id} className={`flex items-center gap-0.5 rounded-sm transition-colors ${isActive ? "bg-[#6B1F24] text-white" : "hover:bg-neutral-50 border border-transparent hover:border-neutral-200"}`}>
            {isRenaming ? (
              <div className="flex items-center gap-1 px-2 py-1">
                <input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") confirmarRename(); if (e.key === "Escape") setRenamingId(null); }}
                  className="w-32 h-6 rounded border border-white/40 bg-white/10 px-1 text-[12px] text-inherit font-mono"
                  autoFocus
                  data-testid={`dashboard-tab-rename-input-${t.id}`}
                />
                <button onClick={confirmarRename} className="hover:opacity-70" data-testid={`dashboard-tab-rename-ok-${t.id}`}><Check size={12} weight="bold" /></button>
                <button onClick={() => setRenamingId(null)} className="hover:opacity-70"><X size={12} weight="bold" /></button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onChange?.(t.id)}
                  onDoubleClick={() => canEdit && iniciarRename(t)}
                  className={`px-3 py-1.5 text-[12px] font-mono uppercase tracking-wider transition-colors ${isActive ? "text-white" : "text-neutral-700"}`}
                  data-testid={`dashboard-tab-${t.id}`}
                >
                  {t.nombre}
                </button>
                {isActive && canEdit && (
                  <>
                    <button
                      onClick={() => iniciarRename(t)}
                      className="text-white/70 hover:text-white p-1"
                      title="Renombrar (doble click también)"
                      data-testid={`dashboard-tab-rename-${t.id}`}
                    >
                      <PencilSimple size={10} weight="bold" />
                    </button>
                    <button
                      onClick={() => borrar(t)}
                      className="text-white/70 hover:text-red-200 p-1 mr-1"
                      title="Eliminar pestaña"
                      data-testid={`dashboard-tab-borrar-${t.id}`}
                    >
                      <Trash size={10} weight="bold" />
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        );
      })}
      {canEdit ? (
        <>
          <Button
            size="sm" variant="outline"
            onClick={crear}
            className="text-[11px] h-7 gap-1 ml-1"
            data-testid="dashboard-tab-nueva"
          >
            <Plus size={12} weight="bold" /> Nueva pestaña
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={() => setConfigOpen(true)}
            className="text-[11px] h-7 gap-1"
            data-testid="dashboard-tab-configurar"
            title="Elegir qué indicadores muestra esta pestaña (compartida con todos los usuarios que la ven)"
          >
            <GearSix size={12} weight="bold" /> Configurar
          </Button>
        </>
      ) : (
        <span className="text-[10px] font-mono text-neutral-500 ml-2" data-testid="dashboard-tabs-readonly">
          Pestañas configuradas por administración
        </span>
      )}

      <Dialog open={configOpen} onOpenChange={(v) => !saving && setConfigOpen(v)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Configurar pestaña "{active?.nombre}"</DialogTitle>
            <DialogDescription>
              Elegí qué indicadores querés ver en esta pestaña. El resto se ocultan sin borrarse.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 max-h-[60vh] overflow-y-auto space-y-4">
            {Object.entries(groups).map(([grp, widgets]) => (
              <div key={grp}>
                <div className="text-[11px] uppercase font-mono tracking-widest text-neutral-500 mb-1">{grp}</div>
                <div className="grid grid-cols-2 gap-1">
                  {widgets.map((w) => {
                    const activo = (active?.widget_keys || []).includes(w.key);
                    return (
                      <label
                        key={w.key}
                        className={`flex items-center gap-2 px-2 py-1.5 border rounded-sm text-[12px] cursor-pointer transition-colors ${activo ? "border-[#6B1F24] bg-[#F5F1E9] text-[#6B1F24]" : "border-neutral-200 hover:bg-neutral-50"}`}
                        data-testid={`config-widget-${w.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={activo}
                          onChange={() => toggleWidget(w.key)}
                          disabled={saving}
                          className="accent-[#6B1F24]"
                        />
                        <span className="font-mono">{w.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)}>Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
